import { IDimensions2D, ISideOffsets } from "./Common"

// Specifies the sensor side where the tail is attached
export enum TailAttachment {
  Top = "Top",
  Bottom = "Bottom",
  Left = "Left",
  Right = "Right",
}
export const allAttachments = [
  TailAttachment.Top,
  TailAttachment.Bottom,
  TailAttachment.Left,
  TailAttachment.Right,
]
export const horizontalAttachments = [TailAttachment.Left, TailAttachment.Right]
export const verticalAttachments = [TailAttachment.Top, TailAttachment.Bottom]

export function opposite(side: TailAttachment) {
  switch (side) {
    case TailAttachment.Top:
      return TailAttachment.Bottom
    case TailAttachment.Bottom:
      return TailAttachment.Top
    case TailAttachment.Left:
      return TailAttachment.Right
    case TailAttachment.Right:
      return TailAttachment.Left
  }
}

// sides that have same sign for tail central offset
export function complementary(side: TailAttachment) {
  switch (side) {
    case TailAttachment.Top:
      return TailAttachment.Left
    case TailAttachment.Bottom:
      return TailAttachment.Right
    case TailAttachment.Left:
      return TailAttachment.Top
    case TailAttachment.Right:
      return TailAttachment.Bottom
  }
}

export interface ISensor extends IDimensions2D {
  name: string | undefined
  withSensor: boolean
  diagonal: number // inches
  activeArea: IDimensions2D
  offset: { x: number; y: number }
  tail: ITail
  cable: {
    height: number
    detail: string | null
  }
  passiveArea: ISideOffsets
  thickness: number
}

export interface ITail {
  width: number // always the side attached to sensor
  length: number // always the side perpendicular to sensor
  centralOffset: number // difference between attachment side center and sensor side center
  attachment: TailAttachment
}

export const standardSensors = [
  {
    diagonal: 0,
    width: 718,
    height: 413,
    activeArea: {
      width: 0,
      height: 0,
    },
    tail: {
      width: 0,
      length: 0,
      centralOffset: 0,
      attachment: TailAttachment.Bottom,
    },
  },
] as ISensor[]

export function getCurrentAttachments(sensor: ISensor) {
  return horizontalAttachments.includes(sensor.tail.attachment)
    ? horizontalAttachments
    : verticalAttachments
}

export function getAlternativeAttachments(sensor: ISensor) {
  return horizontalAttachments.includes(sensor.tail.attachment)
    ? verticalAttachments
    : horizontalAttachments
}

export const defaultSensorsFortouchS: ISensor[] = [
  {
    name: "7 inch (16:9)",
    withSensor: true,
    diagonal: 7,
    activeArea: {
      width: 155.6,
      height: 92,
    },
    offset: {
      x: 0,
      y: 0,
    },
    tail: {
      width: 30,
      length: 0,
      centralOffset: 56.525,
      attachment: TailAttachment.Bottom,
    },
    cable: {
      height: 42.5,
      detail: null,
    },
    passiveArea: {
      t: 3.65,
      b: 9.14,
      l: 4,
      r: 6.47,
    },
    thickness: 0.55,
    width: 168.39,
    height: 102.47,
  },
  {
    name: "9 inch (16:9)",
    withSensor: true,
    diagonal: 9,
    activeArea: {
      width: 199,
      height: 112.7,
    },
    offset: {
      x: 0,
      y: 0,
    },
    tail: {
      width: 34.5,
      length: 0,
      centralOffset: 0,
      attachment: TailAttachment.Left,
    },
    cable: {
      height: 83.05,
      detail: null,
    },
    passiveArea: {
      t: 5,
      b: 5,
      l: 8,
      r: 5,
    },
    thickness: 0.7,
    width: 209.0,
    height: 125.7,
  },
  {
    name: "10,1 inch (16:10)",
    withSensor: true,
    diagonal: 10.1,
    activeArea: {
      width: 219.96,
      height: 138.6,
    },
    offset: {
      x: 0,
      y: 0,
    },
    tail: {
      width: 34.5,
      length: 0,
      centralOffset: -39.2,
      attachment: TailAttachment.Bottom,
    },
    cable: {
      height: 83.05,
      detail: null,
    },
    passiveArea: {
      t: 3.5,
      b: 9,
      l: 5,
      r: 5,
    },
    thickness: 0.7,
    width: 232.46,
    height: 148.6,
  },
  {
    name: "10,4 inch (4:3)",
    withSensor: true,
    diagonal: 10.4,
    activeArea: {
      width: 215.2,
      height: 162.4,
    },
    offset: {
      x: 0,
      y: 0,
    },
    tail: {
      width: 34.5,
      length: 0,
      centralOffset: 0,
      attachment: TailAttachment.Right,
    },
    cable: {
      height: 83.05,
      detail: null,
    },
    passiveArea: {
      t: 6.5,
      b: 10,
      l: 6.5,
      r: 10,
    },
    thickness: 0.7,
    width: 231.7,
    height: 178.9,
  },
  {
    name: "12,1 inch (16:10)",
    withSensor: true,
    diagonal: 12.1,
    activeArea: {
      width: 249,
      height: 187.5,
    },
    offset: {
      x: 0,
      y: 0,
    },
    tail: {
      width: 34.5,
      length: 0,
      centralOffset: 0,
      attachment: TailAttachment.Left,
    },
    cable: {
      height: 71.37,
      detail: null,
    },
    passiveArea: {
      t: 4.5,
      b: 4.5,
      l: 15.5,
      r: 15.5,
    },
    thickness: 0.7,
    width: 258.0,
    height: 218.5,
  },
  {
    name: "13,3 inch (16:9)",
    withSensor: true,
    diagonal: 13.3,
    activeArea: {
      width: 297,
      height: 168.6,
    },
    offset: {
      x: 0,
      y: 0,
    },
    tail: {
      width: 34.5,
      length: 0,
      centralOffset: -101.17,
      attachment: TailAttachment.Bottom,
    },
    cable: {
      height: 83.05,
      detail: null,
    },
    passiveArea: {
      t: 3.65,
      b: 8.87,
      l: 5.44,
      r: 5.44,
    },
    thickness: 0.7,
    width: 309.52,
    height: 179.48,
  },
  {
    name: "15 inch (4:3)",
    withSensor: true,
    diagonal: 15,
    activeArea: {
      width: 308,
      height: 232,
    },
    offset: {
      x: 0,
      y: 0,
    },
    tail: {
      width: 34.5,
      length: 0,
      centralOffset: -8,
      attachment: TailAttachment.Left,
    },
    cable: {
      height: 83.05,
      detail: null,
    },
    passiveArea: {
      t: 4,
      b: 7.5,
      l: 11,
      r: 4,
    },
    thickness: 0.7,
    width: 319.5,
    height: 247.0,
  },
  {
    name: "15,6 inch (16:9)",
    withSensor: true,
    diagonal: 15.6,
    activeArea: {
      width: 346,
      height: 196,
    },
    offset: {
      x: 0,
      y: 0,
    },
    tail: {
      width: 34.5,
      length: 0,
      centralOffset: 0,
      attachment: TailAttachment.Bottom,
    },
    cable: {
      height: 83.05,
      detail: null,
    },
    passiveArea: {
      t: 3.5,
      b: 10.5,
      l: 6,
      r: 6,
    },
    thickness: 0.7,
    width: 360.0,
    height: 208.0,
  },
  {
    name: "17 inch (5:4)",
    withSensor: true,
    diagonal: 17,
    activeArea: {
      width: 340.92,
      height: 273.34,
    },
    offset: {
      x: 0,
      y: 0,
    },
    tail: {
      width: 64,
      length: 0,
      centralOffset: 0,
      attachment: TailAttachment.Bottom,
    },
    cable: {
      height: 123.05,
      detail: null,
    },
    passiveArea: {
      t: 4,
      b: 13.5,
      l: 8,
      r: 8,
    },
    thickness: 0.7,
    width: 358.42,
    height: 289.34,
  },
  {
    name: "18,5 inch (16:9)",
    withSensor: true,
    diagonal: 18.5,
    activeArea: {
      width: 413.8,
      height: 234.4,
    },
    offset: {
      x: 0,
      y: 0,
    },
    tail: {
      width: 64,
      length: 0,
      centralOffset: 0,
      attachment: TailAttachment.Bottom,
    },
    cable: {
      height: 123.05,
      detail: null,
    },
    passiveArea: {
      t: 3.5,
      b: 12,
      l: 7.1,
      r: 7.1,
    },
    thickness: 0.7,
    width: 429.3,
    height: 248.6,
  },
  {
    name: "19 inch (5:4)",
    withSensor: true,
    diagonal: 19,
    activeArea: {
      width: 380.32,
      height: 304.56,
    },
    offset: {
      x: 0,
      y: 0,
    },
    tail: {
      width: 64,
      length: 0,
      centralOffset: 0,
      attachment: TailAttachment.Bottom,
    },
    cable: {
      height: 123.05,
      detail: null,
    },
    passiveArea: {
      t: 3.7,
      b: 12,
      l: 7.34,
      r: 7.34,
    },
    thickness: 0.7,
    width: 396.02,
    height: 319.23999999999995,
  },
  {
    name: "21,5 inch (16:9)",
    withSensor: true,
    diagonal: 21.5,
    activeArea: {
      width: 479.84,
      height: 271.31,
    },
    offset: {
      x: 0,
      y: 0,
    },
    tail: {
      width: 64,
      length: 0,
      centralOffset: 0,
      attachment: TailAttachment.Bottom,
    },
    cable: {
      height: 123.05,
      detail: null,
    },
    passiveArea: {
      t: 3.7,
      b: 12.8,
      l: 6.79,
      r: 6.79,
    },
    thickness: 0.7,
    width: 496.34,
    height: 284.89000000000004,
  },
  {
    name: "23,8 inch (16:9)",
    withSensor: true,
    diagonal: 23.8,
    activeArea: {
      width: 530,
      height: 300,
    },
    offset: {
      x: 0,
      y: 0,
    },
    tail: {
      width: 64,
      length: 0,
      centralOffset: 0,
      attachment: TailAttachment.Bottom,
    },
    cable: {
      height: 78.05,
      detail: null,
    },
    passiveArea: {
      t: 4,
      b: 12.5,
      l: 7,
      r: 7,
    },
    thickness: 0.7,
    width: 546.5,
    height: 314.0,
  },
  {
    name: "23,8 inch (16:9) V2",
    withSensor: true,
    diagonal: 23.8,
    activeArea: {
      width: 530,
      height: 300,
    },
    offset: {
      x: 0,
      y: 0,
    },
    tail: {
      width: 64,
      length: 0,
      centralOffset: 0,
      attachment: TailAttachment.Bottom,
    },
    cable: {
      height: 178.05,
      detail: null,
    },
    passiveArea: {
      t: 4,
      b: 12.5,
      l: 7,
      r: 7,
    },
    thickness: 0.7,
    width: 546.5,
    height: 314.0,
  },
  {
    name: "27 inch (16:9)",
    withSensor: true,
    diagonal: 27,
    activeArea: {
      width: 600.9,
      height: 339.31,
    },
    offset: {
      x: 0,
      y: 0,
    },
    tail: {
      width: 64,
      length: 0,
      centralOffset: 0,
      attachment: TailAttachment.Bottom,
    },
    cable: {
      height: 120.95,
      detail: null,
    },
    passiveArea: {
      t: 13.3,
      b: 14.3,
      l: 13.3,
      r: 13.3,
    },
    thickness: 1.1,
    width: 628.4999999999999,
    height: 365.91,
  },
  {
    name: "32 inch (16:9)",
    withSensor: true,
    diagonal: 32,
    activeArea: {
      width: 698.4,
      height: 392.85,
    },
    offset: {
      x: 0,
      y: 0,
    },
    tail: {
      width: 64,
      length: 0,
      centralOffset: 0,
      attachment: TailAttachment.Bottom,
    },
    cable: {
      height: 95.25,
      detail: null,
    },
    passiveArea: {
      t: 5.126,
      b: 14.952,
      l: 9.748,
      r: 9.753,
    },
    thickness: 0.5,
    width: 718.478,
    height: 412.351,
  },
  {
    name: "32 inch (16:9) V2",
    withSensor: true,
    diagonal: 32,
    activeArea: {
      width: 698.4,
      height: 392.85,
    },
    offset: {
      x: 0,
      y: 0,
    },
    tail: {
      width: 91,
      length: 0,
      centralOffset: 0,
      attachment: TailAttachment.Left,
    },
    cable: {
      height: 150,
      detail: null,
    },
    passiveArea: {
      t: 15,
      b: 15,
      l: 15.5,
      r: 12.5,
    },
    thickness: 0.5,
    width: 728.4,
    height: 420.85,
  },
  {
    name: "43 inch (16:9)",
    withSensor: true,
    diagonal: 43,
    activeArea: {
      width: 944.18,
      height: 532.52,
    },
    offset: {
      x: 0,
      y: 0,
    },
    tail: {
      width: 91,
      length: 0,
      centralOffset: 0,
      attachment: TailAttachment.Bottom,
    },
    cable: {
      height: 139.66,
      detail: null,
    },
    passiveArea: {
      t: 3.1,
      b: 12.92,
      l: 7.62,
      r: 7.62,
    },
    thickness: 0.5,
    width: 960.1999999999999,
    height: 547.76,
  },
  {
    name: "46 inch (16:9)",
    withSensor: true,
    diagonal: 46,
    activeArea: {
      width: 1020,
      height: 575,
    },
    offset: {
      x: 0,
      y: 0,
    },
    tail: {
      width: 91,
      length: 0,
      centralOffset: 0,
      attachment: TailAttachment.Bottom,
    },
    cable: {
      height: 139.66,
      detail: null,
    },
    passiveArea: {
      t: 4.5,
      b: 14.5,
      l: 9,
      r: 9,
    },
    thickness: 0.5,
    width: 1039.0,
    height: 593.0,
  },
  {
    name: "49 inch (16:9)",
    withSensor: true,
    diagonal: 49,
    activeArea: {
      width: 1076.78,
      height: 607,
    },
    offset: {
      x: 0,
      y: 0,
    },
    tail: {
      width: 91,
      length: 0,
      centralOffset: 0,
      attachment: TailAttachment.Bottom,
    },
    cable: {
      height: 109.65,
      detail: null,
    },
    passiveArea: {
      t: 4.5,
      b: 16.04,
      l: 10.11,
      r: 10.11,
    },
    thickness: 0.5,
    width: 1097.32,
    height: 627.22,
  },
  {
    name: "50 inch (16:9)",
    withSensor: true,
    diagonal: 50,
    activeArea: {
      width: 1096.6,
      height: 617.05,
    },
    offset: {
      x: 0,
      y: 0,
    },
    tail: {
      width: 91,
      length: 0,
      centralOffset: 0,
      attachment: TailAttachment.Bottom,
    },
    cable: {
      height: 109.65,
      detail: null,
    },
    passiveArea: {
      t: 4.38,
      b: 16.59,
      l: 11.15,
      r: 11.15,
    },
    thickness: 0.5,
    width: 1117.57,
    height: 639.3499999999999,
  },
  {
    name: "55 inch (16:9) V1",
    withSensor: true,
    diagonal: 55,
    activeArea: {
      width: 1213.6,
      height: 683.9,
    },
    offset: {
      x: 0,
      y: 0,
    },
    tail: {
      width: 87,
      length: 0,
      centralOffset: 0,
      attachment: TailAttachment.Bottom,
    },
    cable: {
      height: 197.3,
      detail: null,
    },
    passiveArea: {
      t: 8,
      b: 20.26,
      l: 11.68,
      r: 11.68,
    },
    thickness: 0.5,
    width: 1241.86,
    height: 707.2599999999999,
  },
  {
    name: "55 inch (16:9) V2",
    withSensor: true,
    diagonal: 55,
    activeArea: {
      width: 1209.6,
      height: 680.4,
    },
    offset: {
      x: 0,
      y: 0,
    },
    tail: {
      width: 91,
      length: 0,
      centralOffset: 0,
      attachment: TailAttachment.Bottom,
    },
    cable: {
      height: 150,
      detail: null,
    },
    passiveArea: {
      t: 7,
      b: 18,
      l: 12,
      r: 12,
    },
    thickness: 0.5,
    width: 1234.6,
    height: 704.4,
  },
  {
    name: "65 inch (16:9)",
    withSensor: true,
    diagonal: 65,
    activeArea: {
      width: 1432.48,
      height: 807.52,
    },
    offset: {
      x: 0,
      y: 0,
    },
    tail: {
      width: 140.5,
      length: 0,
      centralOffset: 0,
      attachment: TailAttachment.Bottom,
    },
    cable: {
      height: 197.297,
      detail: null,
    },
    passiveArea: {
      t: 9,
      b: 24.844,
      l: 13.672,
      r: 13.672,
    },
    thickness: 0.5,
    width: 1466.324,
    height: 834.864,
  },
  {
    name: "65 inch (16:9) V2",
    withSensor: true,
    diagonal: 65,
    activeArea: {
      width: 1429.5,
      height: 804.5,
    },
    offset: {
      x: 0,
      y: 0,
    },
    tail: {
      width: 140.5,
      length: 0,
      centralOffset: 0,
      attachment: TailAttachment.Left,
    },
    cable: {
      height: 147.4,
      detail: null,
    },
    passiveArea: {
      t: 15.4,
      b: 15.4,
      l: 22.4,
      r: 9,
    },
    thickness: 0.5,
    width: 1460.3000000000002,
    height: 835.9,
  },
  {
    name: "75 inch (16:9)",
    withSensor: true,
    diagonal: 75,
    activeArea: {
      width: 1654.84,
      height: 932.86,
    },
    offset: {
      x: 0,
      y: 0,
    },
    tail: {
      width: 140.5,
      length: 0,
      centralOffset: 0,
      attachment: TailAttachment.Bottom,
    },
    cable: {
      height: 156.45,
      detail: null,
    },
    passiveArea: {
      t: 8.5,
      b: 25.5,
      l: 16.5,
      r: 16.5,
    },
    thickness: 0.5,
    width: 1688.84,
    height: 965.86,
  },
  {
    name: "75 inch (16:9) V2",
    withSensor: true,
    diagonal: 75,
    activeArea: {
      width: 1658.66,
      height: 935.94,
    },
    offset: {
      x: 0,
      y: 0,
    },
    tail: {
      width: 140.5,
      length: 0,
      centralOffset: 0,
      attachment: TailAttachment.Left,
    },
    cable: {
      height: 133.1,
      detail: null,
    },
    passiveArea: {
      t: 17.381,
      b: 22.479,
      l: 30,
      r: 15,
    },
    thickness: 0.5,
    width: 1698.5200000000002,
    height: 980.94,
  },
  {
    name: "85 inch (16:9)",
    withSensor: true,
    diagonal: 85,
    activeArea: {
      width: 1876,
      height: 1057,
    },
    offset: {
      x: 0,
      y: 0,
    },
    tail: {
      width: 140.5,
      length: 0,
      centralOffset: 0,
      attachment: TailAttachment.Bottom,
    },
    cable: {
      height: 156.45,
      detail: null,
    },
    passiveArea: {
      t: 10.912,
      b: 24.743,
      l: 18.2,
      r: 18.2,
    },
    thickness: 0.5,
    width: 1911.655,
    height: 1093.4,
  },
]